// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// =============================================================================
import React, { useState } from 'react';
import { BadgeCheck } from 'lucide-react';
import { useI18n } from '../i18n';
import { rechercherCertification } from '../services/api';

/**
 * Vérifier une certification (chantier ⑩, idée de Marc du 21/09/2026).
 *
 * Le diagnostiqueur, le chef de projet ou le maître d'ouvrage tape le prénom et
 * le nom d'un diagnostiqueur : la page montre ses certifications EN COURS selon
 * l'annuaire officiel, tous domaines, l'amiante en tête avec sa mention — c'est
 * elle qui dit si un repérage liste C est couvert.
 *
 * ⛔ Jamais « non certifié » : l'annuaire ne garde aucune certification expirée,
 * et une graphie différente suffit à ne rien trouver. L'absence se dit
 * « aucune certification en cours trouvée », avec ses raisons possibles.
 *
 * Gratuite, 30 recherches par jour (décisions de Marc, 23/09/2026) : le compteur
 * vit côté serveur, l'écran ne fait que l'afficher.
 */

const carte = { background: 'var(--bg-surface)', borderRadius: 12, border: '1px solid var(--gray)', padding: 20 };
const champ = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--gray)',
                background: 'var(--bg-input)', color: 'var(--black)', fontSize: 14 };

const VerifierCertification = () => {
  const { t, lang } = useI18n();
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [resultat, setResultat] = useState(null);
  const [cherche, setCherche] = useState(null);         // le nom tel que recherché
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  const date = (iso) => (iso
    ? new Date(iso).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—');

  const rechercher = async (e) => {
    e.preventDefault();
    setErreur(''); setEnCours(true);
    try {
      const r = await rechercherCertification(prenom.trim(), nom.trim());
      setResultat(r);
      setCherche(`${prenom.trim()} ${nom.trim().toUpperCase()}`);
    } catch (err) {
      setResultat(null);
      setErreur(err.code === 'limite_atteinte' ? t('certif_limite_atteinte')
        : err.code === 'saisie_incomplete' ? t('certif_saisie_incomplete')
        : err.message || t('err_generic'));
    } finally {
      setEnCours(false);
    }
  };

  const Resultat = () => {
    if (!resultat) return null;
    const { statut, certificats } = resultat;
    if (statut === 'SOURCE_INDISPONIBLE' || statut === 'DESACTIVE') {
      return <div style={{ ...carte, color: '#b45309' }}>{t('certif_source_indisponible')}</div>;
    }
    if (statut === 'NOM_INEXPLOITABLE') {
      return <div style={carte}>{t('certif_saisie_incomplete')}</div>;
    }
    if (statut === 'ABSENT' || !certificats.length) {
      return (
        <div style={carte}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--black)', marginBottom: 8 }}>
            {t('certif_aucune').replace('{nom}', cherche)}
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 6px' }}>{t('certif_aucune_raisons')}</p>
          <ul style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, paddingLeft: 20, lineHeight: 1.7 }}>
            <li>{t('certif_raison_graphie')}</li>
            <li>{t('certif_raison_expiree')}</li>
            <li>{t('certif_raison_jamais')}</li>
          </ul>
        </div>
      );
    }
    return (
      <>
        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--black)', margin: '0 0 10px' }}>
          {t('certif_trouvees').replace('{nom}', cherche)}
        </div>
        {/* Deux numéros sous un même nom : peut-être deux personnes homonymes. */}
        {certificats.length > 1 && (
          <p style={{ fontSize: 13, color: '#b45309', margin: '0 0 10px' }}>⚠️ {t('certif_plusieurs')}</p>
        )}
        {/* ⭐ À L'IDENTIQUE du détail « plus d'informations » de l'annuaire
            (demande de Marc, 23/09/2026) : une ligne par ligne publiée, dont
            DEUX pour l'amiante quand il y a mention, chacune avec ses dates,
            son organisme et son numéro. Rien n'est fusionné ni reformulé. */}
        {certificats.map(c => {
          const aUneMention = c.domaines.some(d => d.avec_mention);
          return (
            <div key={c.numero || c.organisme} style={{ ...carte, marginBottom: 12, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['certif_col_domaine', 'certif_col_debut', 'certif_col_fin', 'certif_organisme', 'certif_numero'].map(k => (
                      <th key={k} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--gray)', color: 'var(--black)', whiteSpace: 'nowrap' }}>{t(k)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {c.domaines.map(d => (
                    <tr key={`${d.libelle}-${d.debut}`} style={d.amiante ? { background: 'rgba(37,99,235,0.05)' } : undefined}>
                      <td style={{ padding: '8px', borderBottom: '1px solid var(--gray)' }}>
                        <strong>{d.libelle}</strong>
                        {/* La mention se lit sur SA ligne ; « sans mention » ne
                            s'écrit que si aucune ligne de mention n'existe. */}
                        {d.avec_mention && (
                          <div style={{ fontSize: 12, marginTop: 2, color: '#16a34a' }}>{t('certif_mention_avec')}</div>
                        )}
                        {d.amiante && !d.avec_mention && !aUneMention && (
                          <div style={{ fontSize: 12, marginTop: 2, color: 'var(--text-muted)' }}>{t('certif_mention_sans')}</div>
                        )}
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid var(--gray)', whiteSpace: 'nowrap' }}>{date(d.debut)}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid var(--gray)', whiteSpace: 'nowrap' }}>{date(d.fin)}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid var(--gray)' }}>{c.organisme || '—'}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid var(--gray)', whiteSpace: 'nowrap' }}>{c.numero || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <main className="container mx-auto px-4 py-8" style={{ maxWidth: 820 }}>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 22, fontWeight: 700, color: 'var(--black)', marginBottom: 6 }}>
        <BadgeCheck size={22} /> {t('certif_titre')}
      </h1>
      <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 20px', lineHeight: 1.6 }}>{t('certif_intro')}</p>

      <form onSubmit={rechercher} style={{ ...carte, marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <label>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('certif_prenom')}</span>
            <input style={champ} value={prenom} onChange={e => setPrenom(e.target.value)} maxLength={60} autoFocus />
          </label>
          <label>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{t('certif_nom')}</span>
            <input style={champ} value={nom} onChange={e => setNom(e.target.value)} maxLength={60} />
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {resultat ? t('certif_restantes').replace('{n}', resultat.restantes).replace('{limite}', resultat.limite) : t('certif_source')}
          </span>
          <button type="submit" disabled={enCours || prenom.trim().length < 2 || nom.trim().length < 2}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--primary, #2563eb)', color: '#fff', fontWeight: 600, cursor: enCours ? 'wait' : 'pointer', opacity: (prenom.trim().length < 2 || nom.trim().length < 2) ? 0.5 : 1 }}>
            {enCours ? t('certif_recherche_en_cours') : t('certif_rechercher')}
          </button>
        </div>
      </form>

      {erreur ? <div role="alert" style={{ ...carte, color: '#dc2626' }}>{erreur}</div> : <Resultat />}

      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 20, lineHeight: 1.6 }}>{t('certif_avertissement')}</p>
    </main>
  );
};

export default VerifierCertification;
